import {
  ColumnConfig,
  defineTableSchema,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import {
  FilterItem,
  FilterOperator,
  useFilters,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/filters';
import {
  ColumnFiltersState,
  ColumnPinningState,
  SortingState,
} from '@tanstack/react-table';
import { useQueryStateFixed } from '@Utils/nuqs-fix';
import { parseAsInteger } from 'nuqs';
import { parseAsJsonArray } from '@Utils/nuqs-parsers';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export interface QuickFilterField {
  field: string;
  label: string;
  operator?: FilterOperator;
}

export interface ResourceTableControllerOptions<TData extends object> {
  columnsConfig: ColumnConfig<TData>[];
  defaultSort: { id: string; desc?: boolean };

  // static or dynamic includes/base params
  baseSearchParams?:
    | Record<string, any>
    | ((state: { pageIndex: number; pageSize: number }) => Record<string, any>);

  // turn columnFiltersState into API filter params (JSON:API or whatever)
  buildFilterParams: (filters: ColumnFiltersState) => Record<string, any>;

  quickFilterFields?: QuickFilterField[];
  // Restricts a field's filter-bar operators to these (default: every operator for its type)
  filterOperators?: Partial<Record<string, FilterOperator[]>>;
  debounceMs?: number;
  initialPageSize?: number;
  initialPinning?: ColumnPinningState;

  // row actions renderer
  rowActions?: (row: TData) => React.ReactNode;
}

export interface ResourceTableController<TData extends object> {
  // schema
  columns: ReturnType<typeof defineTableSchema<TData>>['columns'];
  filterDefinitions: ReturnType<typeof defineTableSchema<TData>>['filters'];

  // filter state (for FilterBar / FilterEditor)
  filters: FilterItem[];
  addOrUpdateFilter: (f: FilterItem) => void;
  removeFilter: (field: string) => void;

  // quick filters (for ResourceTableView inputs)
  quickFilterFields: QuickFilterField[];
  quickValues: Record<string, string>;
  setQuickValue: (field: string, value: string) => void;

  // pagination
  pageIndex: number; // 0-based
  pageSize: number;
  setPageIndex: (n: number) => void;
  setPageSize: (n: number) => void;

  // server-side sorting/filter state (driven by table)
  serverSorting: SortingState;
  setServerSorting: (
    updater: SortingState | ((prev: SortingState) => SortingState),
  ) => void;

  serverColumnFilters: ColumnFiltersState;
  setServerColumnFilters: (
    updater:
      | ColumnFiltersState
      | ((prev: ColumnFiltersState) => ColumnFiltersState),
  ) => void;

  // column pinning
  columnPinning: ColumnPinningState;
  setColumnPinning: React.Dispatch<React.SetStateAction<ColumnPinningState>>;

  // derived: what your data hook uses
  searchParams: Record<string, any>;

  // row actions renderer
  rowActions?: (row: TData) => React.ReactNode;
}

function buildSortParam(
  sorting: SortingState,
  fallback: { id: string; desc?: boolean },
): string {
  if (!sorting || sorting.length === 0) {
    return `${fallback.desc ? '-' : ''}${fallback.id}`;
  }
  return sorting.map((s) => `${s.desc ? '-' : ''}${s.id}`).join(',');
}

const parseAsSortingState = parseAsJsonArray.withDefault([]);
const parseAsColumnFiltersState = parseAsJsonArray.withDefault([]);

export function useResourceTableController<TData extends object>(
  options: ResourceTableControllerOptions<TData>,
): ResourceTableController<TData> {
  const {
    columnsConfig,
    defaultSort,
    baseSearchParams,
    buildFilterParams,
    quickFilterFields = [],
    filterOperators,
    debounceMs = 400,
    initialPageSize = 10,
    initialPinning,
    rowActions,
  } = options;

  // Track previous filters to detect changes and reset page
  const previousFiltersRef = useRef<FilterItem[]>([]);
  const isInitialMount = useRef(true);

  // URL query state for pagination (1-based in URL, converted to 0-based internally)
  // Don't use withDefault to prevent nuqs from removing the param when it equals default
  const [pageNumber, setPageNumber] = useQueryStateFixed<number>(
    'page',
    parseAsInteger,
  );

  // Convert 1-based page number from URL to 0-based pageIndex
  // If pageNumber is null/undefined (not in URL), default to page 1 (index 0) internally
  const pageIndex = useMemo(() => {
    const page = pageNumber ?? 1;
    return Math.max(0, page - 1); // Ensure non-negative, convert to 0-based
  }, [pageNumber]);

  const setPageIndex = useCallback(
    (value: number | ((prev: number) => number)) => {
      if (typeof value === 'function') {
        // For function updaters, we need to get the current value from URL state
        setPageNumber((currentPage) => {
          const currentIndex = (currentPage ?? 1) - 1;
          const newIndex = value(currentIndex);

          return newIndex + 1;
        });
      } else {
        // Convert 0-based pageIndex to 1-based page number for URL
        setPageNumber(value + 1);
      }
    },
    [setPageNumber],
  );

  // URL query state for filtering - this is our single source of truth
  const [serverColumnFilters, setServerColumnFilters] =
    useQueryStateFixed<ColumnFiltersState>(
      'filters',
      parseAsColumnFiltersState,
    );

  const [pageSize, setPageSize] = useQueryStateFixed(
    'pageSize',
    parseAsInteger.withDefault(initialPageSize),
  );

  // URL query state for sorting
  const [serverSorting, setServerSorting] = useQueryStateFixed<SortingState>(
    'sort',
    parseAsSortingState,
  );

  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>(
    initialPinning ?? { left: ['select'], right: [] },
  );

  const { columns, filters: schemaFilters } =
    defineTableSchema<TData>(columnsConfig);
  const filterDefinitions = filterOperators
    ? schemaFilters.map((def) => {
        const allowed = filterOperators[def.field];
        return allowed
          ? {
              ...def,
              operators: def.operators.filter((op) =>
                allowed.includes(op.value),
              ),
            }
          : def;
      })
    : schemaFilters;

  // Convert serverColumnFilters (from URL) to FilterItem[] for useFilters initialization
  // This ensures filters from URL are loaded on mount
  const initialFilters = useMemo<FilterItem[]>(() => {
    return (serverColumnFilters ?? []).map((cf) => {
      const filterDef = filterDefinitions.find((def) => def.field === cf.id);
      const value = cf.value as
        | { operator: FilterOperator; value: any }
        | undefined;

      return {
        field: cf.id,
        operator: value?.operator ?? 'contains',
        value: value?.value ?? '',
        displayName: filterDef?.displayName ?? cf.id,
        displayValue: String(value?.value ?? ''),
      } as FilterItem;
    });
  }, [serverColumnFilters, filterDefinitions]);

  const {
    filters,
    addOrUpdateFilter: rawAddOrUpdateFilter,
    removeFilter: rawRemoveFilter,
  } = useFilters(initialFilters);

  // Sync filters from URL when serverColumnFilters changes (e.g., on navigation/refresh)
  // This ensures filters persist when the URL is read after initial mount
  const lastSyncedUrlFilters = useRef<string>('');
  const isInitialSync = useRef(true);
  const filtersForSyncRef = useRef(filters);
  const hasUrlBeenRead = useRef(false);

  // Keep filtersForSyncRef in sync
  useEffect(() => {
    filtersForSyncRef.current = filters;
  }, [filters]);

  // Track when URL has been read (serverColumnFilters is no longer null/undefined)
  useEffect(() => {
    if (serverColumnFilters !== null && serverColumnFilters !== undefined) {
      hasUrlBeenRead.current = true;
    }
  }, [serverColumnFilters]);

  useEffect(() => {
    // Wait for URL to be read before syncing
    if (!hasUrlBeenRead.current) {
      return;
    }

    // Convert serverColumnFilters to FilterItem[] format for comparison
    const urlFilters: FilterItem[] = (serverColumnFilters ?? []).map((cf) => {
      const filterDef = filterDefinitions.find((def) => def.field === cf.id);
      const value = cf.value as
        | { operator: FilterOperator; value: any }
        | undefined;

      return {
        field: cf.id,
        operator: value?.operator ?? 'contains',
        value: value?.value ?? '',
        displayName: filterDef?.displayName ?? cf.id,
        displayValue: String(value?.value ?? ''),
      } as FilterItem;
    });

    const urlFiltersJson = JSON.stringify(
      urlFilters.map((f) => ({
        field: f.field,
        operator: f.operator,
        value: f.value,
      })),
    );

    // Skip if URL filters haven't changed
    if (lastSyncedUrlFilters.current === urlFiltersJson) {
      return;
    }

    // Get current filters from ref to avoid dependency issues
    const currentFilters = filtersForSyncRef.current;
    const currentFiltersJson = JSON.stringify(
      currentFilters.map((f) => ({
        field: f.field,
        operator: f.operator,
        value: f.value,
      })),
    );

    // On initial sync, sync from URL if they don't match
    if (isInitialSync.current) {
      isInitialSync.current = false;
      // Use a small delay to ensure useFilters has initialized
      const timer = setTimeout(() => {
        const currentFiltersAfterInit = filtersForSyncRef.current;
        const currentFiltersJsonAfterInit = JSON.stringify(
          currentFiltersAfterInit.map((f) => ({
            field: f.field,
            operator: f.operator,
            value: f.value,
          })),
        );

        if (urlFiltersJson !== currentFiltersJsonAfterInit) {
          // Sync from URL on initial load
          urlFilters.forEach((urlFilter) => {
            rawAddOrUpdateFilter(urlFilter);
          });
          // Remove filters not in URL
          currentFiltersAfterInit.forEach((f) => {
            if (!urlFilters.some((uf) => uf.field === f.field)) {
              rawRemoveFilter(f.field);
            }
          });
        }
        lastSyncedUrlFilters.current = urlFiltersJson;
      }, 0);

      return () => clearTimeout(timer);
    }

    // After initial sync, only sync if URL changed and filters don't match
    // This handles navigation back/refresh
    if (currentFiltersJson !== urlFiltersJson) {
      // Remove filters that aren't in URL
      currentFilters.forEach((f) => {
        if (!urlFilters.some((uf) => uf.field === f.field)) {
          rawRemoveFilter(f.field);
        }
      });

      // Add/update filters from URL
      urlFilters.forEach((urlFilter) => {
        const existing = currentFilters.find(
          (f) => f.field === urlFilter.field,
        );
        if (
          !existing ||
          existing.operator !== urlFilter.operator ||
          existing.value !== urlFilter.value
        ) {
          rawAddOrUpdateFilter(urlFilter);
        }
      });
    }

    lastSyncedUrlFilters.current = urlFiltersJson;
  }, [
    serverColumnFilters,
    filterDefinitions,
    rawAddOrUpdateFilter,
    rawRemoveFilter,
  ]);

  // quick filter local state
  const [quickValues, setQuickValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    quickFilterFields.forEach((q) => {
      initial[q.field] = '';
    });
    return initial;
  });

  const setQuickValue = useCallback((field: string, value: string) => {
    setQuickValues((prev) => ({ ...prev, [field]: value }));
  }, []);

  // wrapped addOrUpdateFilter: sync quickValues when filter is applied via FilterEditor
  const addOrUpdateFilter = useCallback(
    (filter: FilterItem) => {
      rawAddOrUpdateFilter(filter);
      // If this field has a quick filter, sync quickValues to match the filter value
      // This prevents the quick filter useEffect from overwriting the filter
      if (quickFilterFields.some((q) => q.field === filter.field)) {
        setQuickValues((prev) => ({
          ...prev,
          [filter.field]: String(filter.value),
        }));
      }
    },
    [rawAddOrUpdateFilter, quickFilterFields],
  );

  // wrapped removeFilter: also clear quick filter if mapped
  const removeFilter = useCallback(
    (field: string) => {
      rawRemoveFilter(field);
      if (quickFilterFields.some((q) => q.field === field)) {
        setQuickValues((prev) =>
          prev[field] ? { ...prev, [field]: '' } : prev,
        );
      }
    },
    [rawRemoveFilter, quickFilterFields],
  );

  // debounced: quickValues -> serverColumnFilters (URL)
  // Use ref to access current filters without including in dependencies
  const filtersRef = useRef(filters);
  const isQuickFilterInitialMount = useRef(true);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Initialize quickValues from filters on mount (if filters exist from URL)
  // This prevents quick filter useEffect from clearing filters on initial load
  useEffect(() => {
    if (!quickFilterFields.length || !isQuickFilterInitialMount.current) return;
    if (filters.length === 0) return; // Wait for filters to be loaded

    isQuickFilterInitialMount.current = false;
    // Initialize quickValues from filters if they exist
    const initialQuickValues: Record<string, string> = {};
    filters.forEach((f) => {
      if (quickFilterFields.some((q) => q.field === f.field)) {
        initialQuickValues[f.field] = String(f.value);
      }
    });
    if (Object.keys(initialQuickValues).length > 0) {
      setQuickValues((prev) => ({ ...prev, ...initialQuickValues }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.length]); // Only run when filters are first loaded

  useEffect(() => {
    if (!quickFilterFields.length) return;

    const handle = setTimeout(() => {
      quickFilterFields.forEach(({ field, label, operator = 'contains' }) => {
        const raw = quickValues[field] ?? '';
        const trimmed = raw.trim();
        // Use ref to get current filters value without causing re-runs
        const existing = filtersRef.current.find((f) => f.field === field);

        if (!trimmed) {
          if (existing) removeFilter(field);
          return;
        }

        if (
          !existing ||
          existing.operator !== operator ||
          existing.value !== trimmed
        ) {
          addOrUpdateFilter({
            field,
            operator,
            value: trimmed,
            displayName: label,
            displayValue: trimmed,
          });
        }
      });
    }, debounceMs);

    return () => clearTimeout(handle);
  }, [
    quickFilterFields,
    quickValues,
    addOrUpdateFilter,
    removeFilter,
    debounceMs,
  ]);

  // Initialize previousFiltersRef with initial filters
  useEffect(() => {
    if (isInitialMount.current) {
      previousFiltersRef.current = filters;
      isInitialMount.current = false;
    }
  }, []); // Only run once on mount

  // Reset page to first page when filters change (but not on initial mount)
  useEffect(() => {
    // Skip on initial mount
    if (isInitialMount.current) {
      return;
    }

    // Compare filters to detect changes
    const prevFiltersJson = JSON.stringify(
      previousFiltersRef.current.map((f) => ({
        field: f.field,
        operator: f.operator,
        value: f.value,
      })),
    );
    const currentFiltersJson = JSON.stringify(
      filters.map((f) => ({
        field: f.field,
        operator: f.operator,
        value: f.value,
      })),
    );

    // If filters changed, reset to first page
    if (prevFiltersJson !== currentFiltersJson) {
      setPageIndex(0);
      previousFiltersRef.current = filters;
    }
  }, [filters, setPageIndex]);

  // wrapped setters for react-table to use (with updater function support)
  const setServerSortingWithUpdater = useCallback(
    (updater: SortingState | ((prev: SortingState) => SortingState)) => {
      setServerSorting((prev) =>
        typeof updater === 'function' ? updater(prev ?? []) : updater,
      );
    },
    [setServerSorting],
  );

  const setServerColumnFiltersWithUpdater = useCallback(
    (
      updater:
        | ColumnFiltersState
        | ((prev: ColumnFiltersState) => ColumnFiltersState),
    ) => {
      setServerColumnFilters((prev) =>
        typeof updater === 'function' ? updater(prev ?? []) : updater,
      );
    },
    [setServerColumnFilters],
  );

  // build searchParams for the data hook
  const searchParams = useMemo(() => {
    const base =
      typeof baseSearchParams === 'function'
        ? baseSearchParams({ pageIndex, pageSize })
        : (baseSearchParams ?? {});

    const sort = buildSortParam(serverSorting, defaultSort);
    const filterParams = buildFilterParams(serverColumnFilters);

    const params: Record<string, any> = {
      ...base,
      ...filterParams,
      'page[number]': pageIndex + 1,
      'page[size]': pageSize,
    };

    if (sort) params.sort = sort;

    return params;
  }, [
    baseSearchParams,
    pageIndex,
    pageSize,
    serverSorting,
    serverColumnFilters,
    defaultSort,
    buildFilterParams,
  ]);

  return {
    columns,
    filterDefinitions,
    filters,
    addOrUpdateFilter,
    removeFilter,
    quickFilterFields,
    quickValues,
    setQuickValue,
    pageIndex,
    pageSize: pageSize ?? initialPageSize,
    setPageIndex,
    setPageSize,
    serverSorting: serverSorting ?? [],
    setServerSorting: setServerSortingWithUpdater,
    serverColumnFilters: serverColumnFilters ?? [],
    setServerColumnFilters: setServerColumnFiltersWithUpdater,
    columnPinning,
    setColumnPinning,
    searchParams,
    rowActions,
  };
}
